// src/components/Hero.tsx
import { Box, Button, Container, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FeatureGrid } from './hero/FeatureGrid';

const topFeatures = [
  { icon: 'icons/security.svg', text: '24/7 ADT Security' },
  { icon: 'icons/no-fees.svg', text: 'No Hidden Fees' },
  { icon: 'icons/trusted.svg', text: 'Trusted by 10,000+ Travelers' }
];

const bottomFeatures = [
  { icon: 'icons/lock.svg', text: 'Every Botel is ADT-secured & monitored 24/7' },
  { icon: 'icons/qr-code.svg', text: 'Fast & Hassle-Free Check-in with QR Code Access!' },
  { icon: 'icons/cards.svg', text: 'Secure Payment via Visa, Mastercard, Apple Pay, Stripe' }
];

export default function Hero() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        background: 'background.default',
        backgroundImage: 'url(images/background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1
        }
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          position: 'relative',
          zIndex: 2,
          py: { xs: 4, sm: 6, md: 8, lg: 10 },
          px: { xs: 2, sm: 3, md: 4 }
        }}
      >
        <Stack
          spacing={{ xs: 3, sm: 4, md: 6 }}
          alignItems="center"
          textAlign="center"
          sx={{
            pt: { xs: 8, sm: 10, md: 12, lg: 16 }
          }}
        >
          {/* Main Heading */}
          <Typography
            variant={isMobile ? 'h4' : isTablet ? 'h3' : 'h2'}
            fontWeight="bold"
            color="white"
            sx={{
              lineHeight: { xs: 1.2, sm: 1.1 },
              maxWidth: { xs: '100%', sm: '90%', md: '80%', lg: '70%' },
              mb: { xs: 2, sm: 3 },
              fontSize: { xs: '1.75rem', sm: '2.125rem', md: '3rem', lg: '3.75rem' }
            }}
          >
            Secure, AI-Powered Smart Storage – Book In Under 60 Seconds!
          </Typography>

          {/* Top Features */}
          <Box sx={{ width: '100%', maxWidth: { xs: '100%', md: '80%' } }}>
            <FeatureGrid features={topFeatures} variant="horizontal" />
          </Box>

          {/* CTA Section */}
          <Stack spacing={{ xs: 2, sm: 3 }} alignItems="center" sx={{ width: '100%' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => { }}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 700,
                borderRadius: 2,
                py: { xs: 1.5, sm: 2 },
                px: { xs: 3, sm: 4 },
                fontSize: { xs: 16, sm: 18 },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: '200px' },
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease'
                }
              }}
            >
              secure my storage now
            </Button>

            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={{ xs: 1, sm: 2 }} 
              alignItems="center" 
              justifyContent="center"
              sx={{ width: '100%' }}
            >
              <img src="icons/fire.svg" alt="fire icon" style={{ width: '24px', height: '24px' }} />
              <Typography
                variant="body1"
                color="white"
                sx={{
                  fontSize: { xs: 14, sm: 16, md: 18 },
                  fontWeight: 500,
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                Only 2 Botels Left in Your Area – Reserve Now!
              </Typography>
            </Stack>
          </Stack>

          {/* Bottom Features */}
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: '90%', lg: '80%' },
              mt: { xs: 4, sm: 6, md: 8 }
            }}
          >
            <FeatureGrid features={bottomFeatures} variant="vertical" withPaper />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
